import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, MessageSquare, Camera, CheckCircle2, ShieldCheck } from "lucide-react";

const S = {
  en: {
    title: "One quick setup", sub: "so jobs come straight to this phone",
    pickLang: "Choose your language", cont: "Continue",
    youAre: "You are", at: "working at", verify: "Verify this phone",
    sendCode: "Send me a code", enterCode: "Enter the 6-digit code", codeSent: "Code sent",
    verified: "Phone verified", consentTitle: "Before we start texting you",
    c1: "I agree to receive text messages about work at this property on this number.",
    c2: "Reply STOP any time to stop them. Reply HELP for help.",
    c3: "My number is never shared with residents.",
    agree: "I agree — finish setup",
    how: "How this works", h1: "You'll get a text with a photo and the location.",
    h2: "Tap Accept — or just reply ACCEPT.", h3: "When you're finished, tap Done and send a photo.",
    doneTitle: "You're all set", doneSub: "Nothing else to do. Watch for a text.",
    langName: "English",
  },
  es: {
    title: "Una configuración rápida", sub: "para que los trabajos lleguen a este teléfono",
    pickLang: "Elige tu idioma", cont: "Continuar",
    youAre: "Eres", at: "trabajando en", verify: "Verifica este teléfono",
    sendCode: "Mándame un código", enterCode: "Escribe el código de 6 dígitos", codeSent: "Código enviado",
    verified: "Teléfono verificado", consentTitle: "Antes de empezar a escribirte",
    c1: "Acepto recibir mensajes de texto sobre el trabajo en esta propiedad en este número.",
    c2: "Responde STOP en cualquier momento para detenerlos. Responde HELP para ayuda.",
    c3: "Mi número nunca se comparte con los residentes.",
    agree: "Acepto — terminar",
    how: "Cómo funciona", h1: "Recibirás un mensaje con una foto y la ubicación.",
    h2: "Toca Aceptar — o responde ACEPTO.", h3: "Al terminar, toca Listo y manda una foto.",
    doneTitle: "Todo listo", doneSub: "No hay nada más que hacer. Espera un mensaje.",
    langName: "Español",
  },
};

export default function CrewOnboarding() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [lang, setLang] = useState(null);
  const [step, setStep] = useState(0);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/fs/crew/${token}`);
      setD(data);
      // Seed their language FIRST so every screen (including the finished one) is in it,
      // then resume wherever they actually left off — reloading must never re-do a step.
      setLang(data.contact.language || null);
      if (data.contact.consented || data.contact.status === "verified") setStep(4);
      else if (data.contact.phone_verified) { setVerified(true); setStep(2); }
    } catch { setD({ error: true }); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="grid min-h-screen place-items-center bg-sand-50"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  if (d.error) return (
    <div className="grid min-h-screen place-items-center bg-sand-50 px-6 text-center">
      <p className="text-base text-ink/60">That link isn't valid any more.</p>
    </div>
  );
  const t = S[lang === "es" ? "es" : "en"];

  const pickLang = async (l) => {
    setLang(l); setBusy(true);
    try { await api.post(`/fs/crew/${token}/language`, { language: l }); setStep(1); }
    catch { toast.error("Try again"); }
    setBusy(false);
  };
  const sendCode = async () => {
    setBusy(true);
    try { await api.post(`/fs/crew/${token}/otp/send`); setSent(true); toast.success(t.codeSent); }
    catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post(`/fs/crew/${token}/otp/verify`, { code }); setVerified(true); setStep(2); }
    catch (e) { toast.error(errMsg(e, "That code doesn't match")); }
    setBusy(false);
  };
  const consent = async () => {
    setBusy(true);
    try {
      await api.post(`/fs/crew/${token}/consent`, { agreed: true, notice_version: d.notice_version });
      setStep(4);
    } catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };

  const Btn = ({ onClick, children, testid, tone = "primary", disabled }) => (
    <button data-testid={testid} onClick={onClick} disabled={disabled || busy}
      className={`flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-base font-bold transition-colors disabled:opacity-50 ${
        tone === "ghost" ? "border border-sand-300 bg-white text-ink active:bg-sand-100" : "bg-redline-500 text-white active:bg-redline-600"}`}>
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-sand-50 pb-12" data-testid="crew-onboarding">
      <div className="mx-auto max-w-md px-5 pt-10">
        <div className="text-xs font-bold uppercase tracking-widest text-redline-600">{d.vendor.name}</div>
        <div className="text-xs font-semibold text-ink/40" data-testid="crew-scope-line">
          {(d.communities || []).length > 1
            ? `${d.communities.length} ${lang === "es" ? "propiedades" : "properties"} · ${d.communities.map((c) => c.name).join(" · ")}`
            : d.community.name}
        </div>

        {step === 0 && (
          <div className="mt-6">
            <h1 className="font-heading text-2xl font-extrabold text-ink">Reply 1 for English</h1>
            <h2 className="font-heading text-lg font-bold text-ink/70">Responde 2 para Español</h2>
            <p className="mt-3 text-sm text-ink/55">{d.contact.name} — pick your language and we'll do the rest in it.</p>
            {(d.communities || []).length > 1 && (
              <p className="mt-2 text-xs font-semibold text-ink/45">
                {d.communities.length} properties: {d.communities.map((c) => c.name).join(" · ")}
              </p>
            )}
            <div className="mt-6 space-y-2.5">
              <Btn testid="crew-lang-en" onClick={() => pickLang("en")}>English</Btn>
              <Btn testid="crew-lang-es" tone="ghost" onClick={() => pickLang("es")}>Español</Btn>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-6">
            <h1 className="font-heading text-2xl font-extrabold text-ink">{t.verify}</h1>
            <p className="mt-2 text-sm text-ink/55">
              {t.youAre} <span className="font-bold text-ink">{d.contact.name}</span>, {t.at}{" "}
              <span className="font-bold text-ink">
                {(d.communities || []).length > 1
                  ? d.communities.map((c) => c.name).join(" · ")
                  : d.community.name}
              </span>
              {d.contact.phone_last4 ? ` · •••• ${d.contact.phone_last4}` : ""}
            </p>
            <div className="mt-6 space-y-2.5">
              {!sent ? (
                <Btn testid="crew-send-code" onClick={sendCode}><MessageSquare className="h-5 w-5" /> {t.sendCode}</Btn>
              ) : (
                <>
                  <input data-testid="crew-code-input" value={code} inputMode="numeric" maxLength={6}
                    placeholder={t.enterCode} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full rounded-2xl border border-sand-300 bg-white px-5 py-4 text-center text-2xl font-bold tracking-[0.3em] outline-none focus:ring-2 focus:ring-redline-500/30" />
                  <Btn testid="crew-verify-code" onClick={verify} disabled={code.length !== 6}>{t.cont}</Btn>
                  <button data-testid="crew-resend-code" onClick={sendCode} className="w-full py-2 text-sm font-semibold text-ink/45">↻</button>
                </>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-6">
            {verified && (
              <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" /> {t.verified}
              </div>
            )}
            <h1 className="font-heading text-2xl font-extrabold text-ink">{t.consentTitle}</h1>
            <div className="mt-4 space-y-2">
              {[t.c1, t.c2, t.c3].map((line, i) => (
                <div key={i} className="flex items-start gap-2.5 rounded-xl bg-white px-4 py-3 text-sm leading-relaxed text-ink/75">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{line}
                </div>
              ))}
            </div>
            <div className="mt-6"><Btn testid="crew-consent" onClick={() => setStep(3)}>{t.cont}</Btn></div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-6">
            <h1 className="font-heading text-2xl font-extrabold text-ink">{t.how}</h1>
            <div className="mt-4 space-y-2.5">
              {[[MessageSquare, t.h1], [Check, t.h2], [Camera, t.h3]].map(([Icon, line], i) => (
                <div key={i} className="flex items-start gap-3 rounded-2xl bg-white px-4 py-4">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-redline-50">
                    <Icon className="h-4 w-4 text-redline-500" />
                  </span>
                  <span className="text-sm font-semibold leading-relaxed text-ink/80">{line}</span>
                </div>
              ))}
            </div>
            <div className="mt-6"><Btn testid="crew-finish" onClick={consent}>{t.agree}</Btn></div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-10 rounded-3xl bg-white px-6 py-10 text-center" data-testid="crew-done">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-ink">{t.doneTitle}</h1>
            <p className="mt-2 text-sm text-ink/55">{t.doneSub}</p>
            {(d.communities || []).filter((c) => c.role).length > 0 && (
              <div className="mt-5 border-t border-sand-200 pt-4 text-left">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">
                  {lang === "es" ? "Tus propiedades" : "Your properties"}
                </div>
                {d.communities.filter((c) => c.role).map((c) => (
                  <div key={c.name} className="flex items-center justify-between py-1 text-sm">
                    <span className="font-semibold text-ink">{c.name}</span>
                    <span className="text-xs font-bold uppercase text-ink/40">
                      {c.role === "primary" ? (lang === "es" ? "Principal" : "Primary") : (lang === "es" ? "Respaldo" : "Backup")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
